import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import isAuthenticated from "../../components/Utils/isAuthenticated";
import { getNavGroupRoutes, getNavGroupExternalLinks } from "../../config/routes";
import TestScoringGuide from "./components/TestScoringGuide";
import Footer from "./components/Footer";

const faqItems = [
	{
		question: "How should accommodated exams be handled?",
		answer:
			"We prefer that you wait until all accommodated students finish writing and deliver the entire exams at once. If that is not possible, please submit a separate scanning request for the AR batch and include a copy of the original answer key with the AR sheets.",
	},
	// {
	// 	question:
	// 		"This is my first time using the service — where do I start and what do I need to know?",
	// 	answer:
	// 		"Start by reading through the process overview above — it covers everything from preparing your exam materials to receiving your results. When you're ready, click the link in the sidebar to begin a new request.",
	// },
];

export default function TestScoringPage() {
	const auth = useMemo(() => isAuthenticated(), []);
	const userAccess = useMemo(
		() =>
			auth.access
				? auth.access
						.split(",")
						.map((a) => a.trim())
						.filter(Boolean)
				: [],
		[auth.access],
	);

	const routes = useMemo(() => getNavGroupRoutes("exam", userAccess), [userAccess]);
	const externalLinks = useMemo(() => getNavGroupExternalLinks("exam", userAccess), [userAccess]);

	const hasOptions = routes.length > 0 || externalLinks.length > 0;

	const [openFaq, setOpenFaq] = useState(null);
	const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

	return (
		<div className="bg-digitalWhite min-h-[78svh] flex flex-col">
			<div className="w-full mx-auto px-6 md:px-14">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 py-6 md:py-12">
					{/* Guide — 2/3 width */}
					<div className="col-span-1 md:col-span-2 bg-white rounded-xl shadow-sm px-6 md:px-10 py-8">
						<TestScoringGuide />
					</div>

					{/* Navigation — 1/3 width */}
					<div className="w-full mt-8 md:mt-0">
						<div className="sticky top-6">
							<div className="bg-white rounded-xl shadow-sm px-6 md:px-10 py-8">
								<div className="mb-6">
									<h2 className="text-2xl font-semibold text-primary">Test Scoring</h2>
									<p className="text-gray-600 mt-1">Choose a destination below.</p>
								</div>

								{!hasOptions && (
									<div className="rounded-lg border border-gray-200 bg-gray-50 p-5 text-gray-600">
										No test scoring options available for your account.
									</div>
								)}

								{routes.length > 0 && (
									<div className="mb-6">
										<h3 className="text-base font-semibold text-gray-500 uppercase tracking-wide mb-3">
											Internal Pages
										</h3>
										<ul className="grid gap-3 sm:grid-cols-1">
											{routes.map((route) => (
												<li key={route.path}>
													<Link
														to={route.path}
														className="block rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:border-primary hover:shadow-md"
													>
														<span className="text-primary font-semibold">{route.label}</span>
													</Link>
												</li>
											))}
										</ul>
									</div>
								)}

								{externalLinks.length > 0 && (
									<div>
										<h3 className="text-base font-semibold text-gray-500 uppercase tracking-wide mb-3">
											External Links
										</h3>
										<ul className="grid gap-3 sm:grid-cols-1">
											{externalLinks.map((link) => (
												<li key={link.url}>
													<a
														href={link.url}
														target="_blank"
														rel="noreferrer"
														className="block rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:border-primary hover:shadow-md"
													>
														<span className="text-primary font-semibold">{link.label}</span>
														<span className="block text-xs text-gray-500 mt-1">
															Opens in a new tab ↗
														</span>
													</a>
												</li>
											))}
										</ul>
									</div>
								)}
								{/* ── FAQ ─────────────────────────────────────────────────── */}
								<section>
									<h2 className="text-2xl font-semibold text-primary mb-1">
										Frequently Asked Questions
									</h2>
									<p className="text-gray-500 text-sm mb-5">
										Quick answers to common questions about test scoring.
									</p>

									<div className="flex flex-col gap-3">
										{faqItems.map((faq, i) => (
											<div
												key={i}
												className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
											>
												<button
													onClick={() => toggleFaq(i)}
													className="flex items-center justify-between w-full text-left p-5 transition hover:bg-gray-50"
												>
													<span className="font-semibold text-gray-800 pr-4">{faq.question}</span>
													<ChevronIcon open={openFaq === i} />
												</button>

												<div
													className={`grid transition-all duration-200 ease-in-out ${
														openFaq === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
													}`}
												>
													<div className="overflow-hidden">
														<div className="px-5 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
															{faq.answer}
														</div>
													</div>
												</div>
											</div>
										))}
									</div>
								</section>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</div>
	);
}

const ChevronIcon = ({ open }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		className={`w-5 h-5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
		fill="none"
		viewBox="0 0 24 24"
		stroke="currentColor"
		strokeWidth={2}
	>
		<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
	</svg>
);
